// HUD motion primitives, after the motion-designer method (queen-ascension
// docs/queen-avatar-workflows/motion-designer.md): motion is a pure function of time. Each animated value is a
// closed-form spring step response sampled at t, never integrated frame by frame, so a panel's look at any moment is
// seek(t): deterministic and independent of frame rate. "Reduce effects" is simply t = ∞, the settled state.
//
//   underdamped (ζ < 1):     x(t) = 1 − e^(−ζω₀t)·(cos ω_d t + (ζω₀/ω_d)·sin ω_d t),  ω_d = ω₀·√(1 − ζ²)
//   critically damped (ζ = 1): x(t) = 1 − e^(−ω₀t)·(1 + ω₀t)

import { HudError } from './errors'

/** A damped spring's step response from 0 to 1. */
export interface Spring {
  /** Natural angular frequency ω₀ (rad/s). */
  readonly omega: number
  /** Damping ratio ζ in (0, 1]: below 1 it overshoots, at 1 it never passes 1. */
  readonly zeta: number
  /** Damped angular frequency ω_d (0 when critically damped). */
  readonly omegaDamped: number
  /** Seconds after which the response stays within SETTLE_EPSILON of 1; it reads exactly 1 from then on. */
  readonly settle: number
}

/** Settled means within this of the target: a thousandth of the move, below a pixel for any HUD motion. */
const SETTLE_EPSILON = 1e-3
/** Fixed-point iterations solving (1 + u)·e^(−u) = ε for the critically damped settle time (converges in a few). */
const CRITICAL_SETTLE_ITERATIONS = 8
/** Live changes one Track keeps; an older one is folded in early (it snaps its last bit). */
const TRACK_CAPACITY = 8

export function spring(omega: number, zeta: number): Spring {
  if (!(omega > 0) || !(zeta > 0) || zeta > 1) throw new HudError(`spring needs ω₀ > 0 and ζ in (0, 1], got ω₀=${omega} ζ=${zeta}`)
  if (zeta === 1) {
    // (1 + u)·e^(−u) = ε  ⇔  u = ln(1/ε) + ln(1 + u), with u = ω₀t.
    let u = Math.log(1 / SETTLE_EPSILON)
    for (let i = 0; i < CRITICAL_SETTLE_ITERATIONS; i++) u = Math.log(1 / SETTLE_EPSILON) + Math.log(1 + u)
    return { omega, zeta, omegaDamped: 0, settle: u / omega }
  }
  const omegaDamped = omega * Math.sqrt(1 - zeta * zeta)
  // |x − 1| ≤ e^(−ζω₀t)·√(1 + (ζω₀/ω_d)²): the envelope of the cos/sin pair.
  const amplitude = Math.hypot(1, (zeta * omega) / omegaDamped)
  return { omega, zeta, omegaDamped, settle: Math.log(amplitude / SETTLE_EPSILON) / (zeta * omega) }
}

/** The HUD's named springs: every motion uses one of these, so the feel stays consistent. */
export const SPRINGS = {
  /** Sheets and rows arriving: quick, with a hint of overshoot. */
  snap: spring(24, 0.78),
  /** Pops (chips, the stamp): a visible bounce. */
  pop: spring(20, 0.45),
  /** Rolling figures: critically damped, so a number never shows more than it is. */
  roll: spring(11, 1),
  /** Folding away: fast, no overshoot. */
  fold: spring(32, 1),
} as const satisfies Readonly<Record<string, Spring>>

/** The step response of `s` at `t` seconds after it starts: 0 before, exactly 1 once settled. */
export function stepAt(s: Spring, t: number): number {
  if (t <= 0) return 0
  if (t >= s.settle) return 1
  const decay = Math.exp(-s.zeta * s.omega * t)
  if (s.omegaDamped === 0) return 1 - decay * (1 + s.omega * t)
  return 1 - decay * (Math.cos(s.omegaDamped * t) + ((s.zeta * s.omega) / s.omegaDamped) * Math.sin(s.omegaDamped * t))
}

/** Linear progress through [start, start + duration], clamped to [0, 1] (typing, sweeps and fades). */
export function progress(t: number, start: number, duration: number): number {
  return Math.min(1, Math.max(0, (t - start) / duration))
}

/**
 * A value that springs to each new target: one spring per change, each starting at its own time (the motion-designer
 * track()). value(t) = base + Σ (target_i − target_{i−1})·step(t − t_i). Changes that have settled fold into the base
 * when the next one arrives, so reads never mutate.
 */
export class Track {
  private base: number
  private aim: number
  private readonly deltas = new Float64Array(TRACK_CAPACITY)
  private readonly starts = new Float64Array(TRACK_CAPACITY)
  private live = 0

  constructor(
    private readonly curve: Spring,
    initial: number,
  ) {
    this.base = initial
    this.aim = initial
  }

  get target(): number {
    return this.aim
  }

  /** Springs toward `value` from time `now` (a no-op when it already aims there). */
  set(value: number, now: number): void {
    if (value === this.aim) return
    this.fold(now)
    if (this.live === TRACK_CAPACITY) this.foldOldest()
    this.deltas[this.live] = value - this.aim
    this.starts[this.live] = now
    this.live++
    this.aim = value
  }

  /** Jumps to `value` with no motion (a hidden panel, "Reduce effects"). */
  snap(value: number): void {
    this.base = value
    this.aim = value
    this.live = 0
  }

  /** The value at time `t`. */
  at(t: number): number {
    let value = this.base
    for (let i = 0; i < this.live; i++) value += (this.deltas[i] as number) * stepAt(this.curve, t - (this.starts[i] as number))
    return value
  }

  /** When the last change settles (−∞ when there is none in flight). */
  get settlesAt(): number {
    return this.live === 0 ? Number.NEGATIVE_INFINITY : (this.starts[this.live - 1] as number) + this.curve.settle
  }

  /** Folds every change settled by `now` into the base (changes start in time order). */
  private fold(now: number): void {
    let settled = 0
    while (settled < this.live && now - (this.starts[settled] as number) >= this.curve.settle) {
      this.base += this.deltas[settled] as number
      settled++
    }
    if (settled === 0) return
    this.deltas.copyWithin(0, settled, this.live)
    this.starts.copyWithin(0, settled, this.live)
    this.live -= settled
  }

  private foldOldest(): void {
    this.base += this.deltas[0] as number
    this.deltas.copyWithin(0, 1, this.live)
    this.starts.copyWithin(0, 1, this.live)
    this.live--
  }
}
